import { useMemo } from 'react';
import { DEFAULT_POSTAL_CODE_LOCALE } from '../../../constants';
import { StateList, useGetStateList } from '../../../hooks/api-hooks';
import { useConfigShallowStore } from '../../../stores';
import type { SchemaForm } from '../components/dynamic-form-container/types';

/** Injects locale-aware state options and resetOnSchemaChange flags into raw backend schema forms. */
export const applyLocaleToSchemaForms = (schemaForms: SchemaForm[], stateList: StateList): SchemaForm[] => {
  const stateOptions = stateList.map(({ label, value = '' }) => ({ label: label ?? value, value }));
  return schemaForms.map((schemaForm) => ({
    ...schemaForm,
    fields: Object.fromEntries(
      Object.entries(schemaForm.fields).map(([key, field]) => [
        key,
        field.meta.dataKey === 'state'
          ? { ...field, options: stateOptions, resetOnSchemaChange: true }
          : field.meta.type === 'postalCode'
          ? { ...field, resetOnSchemaChange: true }
          : field,
      ])
    ),
  }));
};

export const useLocaleSchemaForms = (schemaForms: SchemaForm[]): SchemaForm[] => {
  const { postalCodeLocale } = useConfigShallowStore('postalCodeLocale');
  const { stateList } = useGetStateList(postalCodeLocale || DEFAULT_POSTAL_CODE_LOCALE);

  return useMemo(() => applyLocaleToSchemaForms(schemaForms, stateList), [schemaForms, stateList]);
};
